'use client';

import type { ReactNode } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import type { FinderData } from '@/lib/types';
import { DAYS, countActive, type FilterState } from '@/lib/filters';
import { shortDay } from '@/lib/format';
import { RemovableChip } from './chips';
import { ToggleSwitch } from '@church/nextjs-ui/components/ToggleSwitch';
import { WoodsideLogo } from './CampusIcon';
import { SEE_JESUS } from '@/lib/seeJesus';

interface FilterBarProps {
  options: FinderData['options'] | null;
  filters: FilterState;
  onChange: (next: FilterState) => void;
  onOpenFilters: () => void;
  /** The campus dropdown, rendered beside the search box */
  campusControl?: ReactNode;
}

/**
 * Search box + Filters button, with the applied filters shown as removable
 * chips underneath so nobody has to open the sheet to see why the list is
 * short.
 */
export function FilterBar({
  options,
  filters,
  onChange,
  onOpenFilters,
  campusControl,
}: FilterBarProps) {
  const active = countActive(filters);
  const label = (list: { id: number; label: string }[] | undefined, id: number | null) =>
    list?.find((o) => o.id === id)?.label ?? String(id);

  const chips: { key: string; text: string; remove: () => void }[] = [];
  if (filters.days.length > 0 && filters.days.length < DAYS.length)
    chips.push({
      key: 'days',
      text: DAYS.filter((d) => filters.days.includes(d))
        .map(shortDay)
        .join(', '),
      remove: () => onChange({ ...filters, days: [] }),
    });
  if (filters.lifeStage !== null)
    chips.push({
      key: 'stage',
      text: label(options?.lifeStages, filters.lifeStage),
      remove: () => onChange({ ...filters, lifeStage: null }),
    });
  if (filters.seeJesus)
    chips.push({
      key: 'sj',
      text: 'See Jesus Groups',
      remove: () => onChange({ ...filters, seeJesus: false }),
    });
  if (filters.kids !== null)
    chips.push({
      key: 'kids',
      text: filters.kids === 1 ? 'Kids welcome' : 'Adults only',
      remove: () => onChange({ ...filters, kids: null }),
    });
  if (filters.household !== null)
    chips.push({
      key: 'household',
      text: label(options?.households, filters.household),
      remove: () => onChange({ ...filters, household: null }),
    });
  if (filters.gender !== null)
    chips.push({
      key: 'gender',
      text: label(options?.genders, filters.gender),
      remove: () => onChange({ ...filters, gender: null }),
    });
  for (const city of filters.cities)
    chips.push({
      key: `city-${city}`,
      text: city,
      remove: () => onChange({ ...filters, cities: filters.cities.filter((c) => c !== city) }),
    });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        {campusControl && <div className="sm:shrink-0">{campusControl}</div>}
        <div className="flex flex-1 items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-4 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={filters.search}
              onChange={(e) => onChange({ ...filters, search: e.target.value })}
              placeholder="Search by city, leader, or keyword"
              aria-label="Search groups"
              className="w-full rounded-full border border-gray-200 bg-white py-3 pr-10 pl-11 text-sm text-[#1c2b39] shadow-sm placeholder:text-gray-400 focus:border-[#299c8f] focus:ring-2 focus:ring-[#299c8f]/30 focus:outline-none"
            />
            {filters.search && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => onChange({ ...filters, search: '' })}
                className="absolute top-1/2 right-2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <ToggleSwitch<'all' | 'sj'>
            options={[
              { value: 'all', icon: <WoodsideLogo className="h-7 w-7" /> },
              {
                value: 'sj',
                icon: (
                  <img src={SEE_JESUS.markUrl} alt="See Jesus" className="h-7 w-7 object-contain" />
                ),
              },
            ]}
            value={filters.seeJesus ? 'sj' : 'all'}
            onChange={(v) => onChange({ ...filters, seeJesus: v === 'sj' })}
            size="lg"
            className="shrink-0"
          />
          <button
            type="button"
            onClick={onOpenFilters}
            className="relative inline-flex shrink-0 items-center gap-2 rounded-full bg-[#1c2b39] px-5 py-3 text-sm font-bold text-white shadow-sm transition-all hover:scale-105 hover:shadow-md active:scale-95"
          >
            <SlidersHorizontal className="h-4 w-4" strokeWidth={2.5} />
            <span className="hidden sm:inline">Filters</span>
            {active > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#299c8f] px-1.5 text-xs font-bold text-white">
                {active}
              </span>
            )}
          </button>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="scrollbar-hide -mx-1 flex items-center gap-2 overflow-x-auto px-1 py-0.5">
          {chips.map((c) => (
            <RemovableChip key={c.key} onRemove={c.remove}>
              {c.text}
            </RemovableChip>
          ))}
          <button
            type="button"
            onClick={() =>
              onChange({
                ...filters,
                lifeStage: null,
                kids: null,
                household: null,
                gender: null,
                days: [],
                cities: [],
              })
            }
            className="shrink-0 px-2 text-xs font-semibold text-gray-500 hover:text-[#1c2b39]"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
